import { injectable, inject } from 'inversify';
import { Product, ProductCategory } from '../../domain/models';
import type { ProductRepository, ProductCategoryRepository } from '../../domain/repositories';
import type { DatabaseDataSource } from '../datasources/database/DatabaseDataSource';
import { DatabaseRecord } from '../datasources/database/DatabaseDataSource';
import { TYPES } from '../../di/types';
import { databaseConfig } from '../../config/database';

/**
 * Database record interface for Product (extends domain model with database fields)
 */
interface ProductRecord extends DatabaseRecord {
  // Product domain fields
  name: string;
  sku: string;
  barcode?: string;
  description?: string;
  salePriceHT: number;
  currency: string;
  taxRate: number;
  categoryId: string;
  imageUrl?: string;
  // stock: number; // Removed - now tracked in BranchInventory
  isActive: boolean;

  // Additional database fields for multi-branch support
  branchInventory?: Array<{
    branchId: string;
    stock: number;
    minStock: number;
    maxStock: number;
  }>;
}

/**
 * Database record interface for ProductCategory
 */
interface ProductCategoryRecord extends DatabaseRecord {
  // ProductCategory domain fields
  name: string;
  description?: string;
  productsCount?: number;  // Product count returned from API
  isActive: boolean;
  isFreeSaleOrService?: boolean;
  isFreePrice?: boolean;
}

/**
 * Implementation of ProductRepository using DatabaseDataSource
 * This follows clean architecture by implementing domain repository interface
 * and using data source abstraction for actual data operations
 */
@injectable()
export class ProductRepositoryImpl implements ProductRepository {
  private readonly COLLECTION_NAME = 'products';
  private readonly baseUrl: string;

  constructor(
    @inject(TYPES.DatabaseDataSource) private readonly _dataSource: DatabaseDataSource
  ) {
    this.baseUrl = databaseConfig.mongodb.apiBaseUrl;
  }


  async countProductsByCategory(categoryId: string): Promise<number> {
    const result = await this._dataSource.count(this.COLLECTION_NAME, {
      categoryId, isActive: true
    });
    return result;
  }

  async findById(id: string): Promise<Product | null> {
    const record = await this._dataSource.findById<ProductRecord>(this.COLLECTION_NAME, id);
    return record ? this.mapToProduct(record) : null;
  }

  async findByBarcode(barcode: string, _branchId: string): Promise<Product | null> {
    const record = await this._dataSource.findOne<ProductRecord>(this.COLLECTION_NAME, {
      barcode,
      isActive: true
    });

    if (!record) return null;

    // Note: Branch stock checking moved to BranchInventoryRepository
    return this.mapToProduct(record);
  }

  async findBySKU(sku: string, _branchId: string): Promise<Product | null> {
    const record = await this._dataSource.findOne<ProductRecord>(this.COLLECTION_NAME, {
      sku,
      isActive: true
    });

    if (!record) return null;

    // Note: Branch stock checking moved to BranchInventoryRepository
    return this.mapToProduct(record);
  }

  async search(query: string, _branchId: string): Promise<Product[]> {
    try {
      // Prefer REST API — local Realm/hybrid store is often empty/unavailable in browser.
      const response = await fetch(
        `${this.baseUrl}/api/products/search?q=${encodeURIComponent(query)}`,
        {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
        },
      );

      if (!response.ok) {
        throw new Error(`Failed to search products: ${response.statusText}`);
      }

      const result = await response.json();
      const rows = result.data || result.items || result || [];
      const products = (Array.isArray(rows) ? rows : []).map((record: ProductRecord) =>
        this.mapToProduct(record),
      );
      return products.filter((p: Product) => p.isActive !== false);
    } catch (error) {
      console.error('❌ Failed to search products from API:', error);
      try {
        const searchFields = ['name', 'sku', 'barcode', 'description'];
        const result = await this._dataSource.search<ProductRecord>(
          this.COLLECTION_NAME,
          query,
          searchFields,
          {
            filters: { isActive: true },
            sortBy: 'name',
            sortOrder: 'asc',
          },
        );
        return result.data.map((record) => this.mapToProduct(record));
      } catch (fallbackError) {
        console.error('❌ Product search fallback also failed:', fallbackError);
        return [];
      }
    }
  }

  async findByCategory(
    categoryId: string,
    _branchId: string,
    page = 1,
    limit = 50
  ): Promise<{
    products: Product[];
    total: number;
    hasMore: boolean;
  }> {
    try {
      // Use dedicated category endpoint from backend API
      const response = await fetch(
        `${this.baseUrl}/api/products/category/${categoryId}?page=${page}&limit=${limit}`,
        {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch products by category: ${response.statusText}`);
      }

      const result = await response.json();
      const products = (result.data || []).map((record: any) => this.mapToProduct(record));
      const total = result.meta?.total || products.length;

      return {
        products,
        total,
        hasMore: products.length === limit && total > page * limit
      };
    } catch (error) {
      console.error('❌ Failed to fetch products by category:', error);
      // Fallback to generic endpoint
      const offset = (page - 1) * limit;
      const result = await this._dataSource.findMany<ProductRecord>(this.COLLECTION_NAME, {
        filters: {
          categoryId,
          isActive: true
        },
        sortBy: 'name',
        sortOrder: 'asc',
        offset,
        limit
      });

      const products = result.data.map(record => this.mapToProduct(record));
      return {
        products,
        total: result.total,
        hasMore: result.hasMore
      };
    }
  }

  async findAll(
    _branchId: string,
    page = 1,
    limit = 50
  ): Promise<{
    products: Product[];
    total: number;
    hasMore: boolean;
  }> {
    try {
      // Prefer REST API — local Realm/hybrid store is often empty while online.
      const response = await fetch(
        `${this.baseUrl}/api/products?page=${page}&limit=${limit}&activeOnly=true`,
        {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch products: ${response.statusText}`);
      }

      const result = await response.json();
      const products = (result.data || []).map((record: any) => this.mapToProduct(record));
      const total = result.meta?.total || result.total || products.length;

      return {
        products,
        total,
        hasMore: products.length === limit && page * limit < total,
      };
    } catch (error) {
      console.error('❌ Failed to fetch all products from API:', error);
      const offset = (page - 1) * limit;
      const result = await this._dataSource.findMany<ProductRecord>(this.COLLECTION_NAME, {
        filters: { isActive: true },
        sortBy: 'name',
        sortOrder: 'asc',
        offset,
        limit
      });

      return {
        products: result.data.map(record => this.mapToProduct(record)),
        total: result.total,
        hasMore: result.hasMore
      };
    }
  }

  async create(productData: Omit<Product, 'id' | 'createdAt' | 'updatedAt'>): Promise<Product> {
    const now = new Date();
    const record = await this._dataSource.create<ProductRecord>(this.COLLECTION_NAME, {
      ...productData,
      id: `prod_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      createdAt: now,
      updatedAt: now,
    });

    return this.mapToProduct(record);
  }

  async update(id: string, productData: Partial<Product>): Promise<Product> {
    const record = await this._dataSource.update<ProductRecord>(this.COLLECTION_NAME, id, {
      ...productData,
      updatedAt: new Date()
    });
    return this.mapToProduct(record);
  }

  async delete(id: string): Promise<void> {
    // Soft delete by setting isActive to false
    await this._dataSource.update<ProductRecord>(this.COLLECTION_NAME, id, {
      isActive: false,
      updatedAt: new Date()
    });
  }

  // Stub implementation for interface compatibility
  async updateStock(_id: string, _quantity: number): Promise<Product> {
    // This method is deprecated - stock updates now handled by BranchInventoryRepository
    throw new Error('updateStock is deprecated - use BranchInventoryRepository instead');
  }

  /**
   * Map database record to domain model
   */
  private mapToProduct(record: ProductRecord): Product {
    // Use visibleId as the primary ID if available (for matching with inventory)
    // The visibleId is the human-readable ID like "prod_xxx" that's used for references
    const productId = (record as any).visibleId || record.id;
    
    return {
      id: productId,
      name: record.name,
      sku: record.sku,
      barcode: record.barcode,
      description: record.description,
      salePriceHT: record.salePriceHT,
      currency: record.currency,
      taxRate: record.taxRate,
      categoryId: record.categoryId,
      imageUrl: record.imageUrl,
      // stock field removed - now tracked in BranchInventory
      isActive: record.isActive,
      createdAt: record.createdAt || new Date(),
      updatedAt: record.updatedAt || new Date()
    };
  }
}

/**
 * Implementation of ProductCategoryRepository using DatabaseDataSource
 */
@injectable()
export class ProductCategoryRepositoryImpl implements ProductCategoryRepository {
  private readonly COLLECTION_NAME = 'categories';
  private readonly baseUrl: string;

  constructor(
    @inject(TYPES.DatabaseDataSource) private readonly _dataSource: DatabaseDataSource
  ) {
    this.baseUrl = databaseConfig.mongodb.apiBaseUrl;
  }

  async findAll(): Promise<ProductCategory[]> {
    try {
      // Use dedicated API endpoint for categories
      const response = await fetch(`${this.baseUrl}/api/categories`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch categories: ${response.statusText}`);
      }

      const result = await response.json();
      const categories = result.data || [];
      
      // Use productsCount from API response (returned by backend)
      return categories.map((record: any) => {
        return this.mapToCategory(record, record.productsCount ?? 0);
      });
    } catch (error) {
      console.error('❌ Failed to fetch categories:', error);
      // Fallback to data source
      const result = await this._dataSource.findMany<ProductCategoryRecord>(this.COLLECTION_NAME, {
        filters: { isActive: true },
        sortBy: 'name',
        sortOrder: 'asc'
      });
      return result.data.map(record => {
        return this.mapToCategory(record, record.productsCount ?? 0);
      });
    }
  }

  async findById(id: string): Promise<ProductCategory | null> {
    try {
      // Use dedicated API endpoint for category lookup
      const response = await fetch(`${this.baseUrl}/api/categories/${id}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });

      if (!response.ok) {
        if (response.status === 404) {
          return null;
        }
        throw new Error(`Failed to fetch category: ${response.statusText}`);
      }

      const result = await response.json();
      const record = result.data || result;
      return this.mapToCategory(record, record.productsCount ?? 0);
    } catch (error) {
      console.error('❌ Failed to fetch category by ID:', error);
      // Fallback to data source
      const record = await this._dataSource.findById<ProductCategoryRecord>(this.COLLECTION_NAME, id);
      return record
        ? this.mapToCategory(record, record.productsCount ?? 0)
        : null;
    }
  }

  async create(categoryData: Omit<ProductCategory, 'id' | 'createdAt' | 'updatedAt'>): Promise<ProductCategory> {
    const now = new Date();
    const record = await this._dataSource.create<ProductCategoryRecord>(this.COLLECTION_NAME, {
      ...categoryData,
      id: `cat_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      createdAt: now,
      updatedAt: now,
    });

    // New category has 0 products
    return this.mapToCategory(record, 0);
  }

  async update(id: string, categoryData: Partial<ProductCategory>): Promise<ProductCategory> {
    const record = await this._dataSource.update<ProductCategoryRecord>(this.COLLECTION_NAME, id, {
      ...categoryData,
      updatedAt: new Date()
    });
    // Preserve existing count or use 0
    return this.mapToCategory(record, record.productsCount ?? 0);
  }

  async delete(id: string): Promise<void> {
    // Soft delete by setting isActive to false
    await this._dataSource.update<ProductCategoryRecord>(this.COLLECTION_NAME, id, {
      isActive: false,
      updatedAt: new Date()
    });
  }

  /**
   * utils
   */
  private mapToCategory(record: ProductCategoryRecord, productsCount: number): ProductCategory {
    // Use visibleId as the primary ID if available
    const categoryId = (record as any).visibleId || record.id;
    
    return {
      id: categoryId,
      name: record.name,
      description: record.description,
      isFreeSaleOrService: record.isFreeSaleOrService ?? false,
      isFreePrice: record.isFreePrice ?? false,
      isActive: record.isActive,
      productsCount: productsCount,
      createdAt: record.createdAt || new Date(),
      updatedAt: record.updatedAt || new Date()
    };
  }
} 